import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { useHistory } from 'react-router';
import { PlayArrow } from '@material-ui/icons';
import { FaEye } from 'react-icons/fa6';

import { dateTimeBodyRender, TableColumn } from '@app/components/common/table';
import { Txt } from '@app/components/common';
import { ROLES } from '@app/navigation/types';
import { sortEntityByName } from '@app/utils';
import { getRoleName } from '@app/utils/jwt';
import { MasterOutletUpdate } from '@app/api/models';

import MapViewModal from './map-modal';
import FrequencyVisitsModal from '../new-outlets/frequency-visits-modal';

const PROCESS_TYPE_OPTIONS = [
  { id: 'others', name: 'Другие' },
  { id: 'channels', name: 'Каналы' },
];

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === '' || value === '<nil>';

const isEmptyAddress = (address: unknown): boolean => {
  if (!address || typeof address !== 'object') {
    return true;
  }
  return Object.values(address as Record<string, unknown>).every((value) => {
    if (isBlank(value)) {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object' && value !== null && 'name' in value) {
      return isBlank((value as EntityBase).name);
    }
    return false;
  });
};

export const useColumns = ({ canEdit, approvalQueue = false }: { canEdit: boolean; approvalQueue?: boolean }): TableColumn<MasterOutletUpdate>[] => {
  const history = useHistory();
  const roleName = getRoleName();

  return useMemo(() => {
    const columns: TableColumn<MasterOutletUpdate>[] = [];

    if (!approvalQueue && roleName !== ROLES.TRADE_MARKETING) {
      columns.push({
        name: 'process_type',
        label: 'Тип процесса',
        options: {
          filter: {
            name: 'process_type',
            type: 'multi-select',            
            onOptionsFetch: () => PROCESS_TYPE_OPTIONS,
            ...(roleName === ROLES.ADMIN
              ? {}
              : {
                  defaultValue: [PROCESS_TYPE_OPTIONS[0]],
                }),
          },
          sort: false,
          hidden: true,
        },
      });
    }

    columns.push(
      {
        name: 'outlet_id',
        label: 'ID мастер-ТТ',
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: 'data',
        label: 'Наименование',
        options: {
          filter: {
            name: 'name',
            type: 'text',
          },
          sort: false,
          customBodyRender: (_, { rowData }) => {
            const name = !isBlank(rowData.data.name)
              ? rowData.data.name
              : rowData.master_outlet?.name;
            const title = !isBlank(rowData.data.title)
              ? rowData.data.title
              : rowData.master_outlet?.title;
            return (
              <>
                <Txt color="brand.500" style={{ fontSize: 16 }}>
                  {name}
                </Txt>
                <Box display="flex">
                  <Txt color="gray.500">Вывеска:&nbsp;</Txt>
                  <Txt>{title}</Txt>
                </Box>
                {!!rowData.data.subnet && <Box display="flex">
                  <Txt color="gray.500">Подсеть:&nbsp;</Txt>
                  <Txt>{rowData.data.subnet}</Txt>
                </Box>}
              </>
            );
          },
        },
      },
      {
        name: 'data',
        label: 'Частота посещений в мес.',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_, { rowData }) => (
            <>
              <FrequencyVisitsModal
                value={
                  rowData.data.visits_day !== undefined
                    ? rowData.data.visits_day
                    : rowData.master_outlet?.visits_day
                }
              />
            </>
          ),
        },
      },
      {
        name: 'data',
        label: 'Канал/Подканал',
        options: {
          filter: {
            name: 'sub_channel_id',
            sourceName: 'outlet_sub_channel',
            type: 'multi-select',
            onOptionsFetch: (channels: EntityBase[]) => sortEntityByName(channels.map(({ id, name, channel }) => {
              return { id, name: `${channel}: ${name}` };
            }))
          },
          sort: false,
          hidden: true,
        },
      },
      {
        name: 'data',
        label: 'Номер маршрута',
        options: {
          filter: {
            name: 'route_number_id',
            sourceName: 'route_number',
            type: 'autocomplete-paginated',
            onApply: (value) => [value?.id]
          },
          sort: false,
          hidden: true,
        },
      },
      {
        name: 'data',
        label: 'Адрес',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_, { rowData }) => {
            const address = !isEmptyAddress(rowData.data.address)
              ? rowData.data.address
              : rowData.master_outlet?.address;
            return (
              <Flex alignItems='center' minW={200}>
                <MapViewModal
                  title={!isBlank(rowData.data.name) ? rowData.data.name : rowData.master_outlet?.name}
                  coords={address?.latlng}
                />
                <Txt wordBreak='break-word' whiteSpace='pre-wrap'>{address?.full_address}</Txt>
              </Flex>
            );
          },
        },
      },
      {
        name: 'create_dt',
        label: 'Дата создания',
        options: {
          filter: {
            name: 'create_date_range',
            type: 'date-range',
            onApply: (value) => value.map((date) => date.split('T')[0])
          },
          sort: false,
          customBodyRender: dateTimeBodyRender(false, false),
        },
      },
      {
        name: 'user',
        label: 'Пользователь',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: { full_name: string }) => {
            return value?.full_name || '';
          },
        },
      },
      {
        name: 'update_dt',
        label: 'Дата обновления',
        options: {
          filter: false,
          sort: false,
          customBodyRender: dateTimeBodyRender(false, false),
        },
      },
      {
        name: 'status',
        label: 'Статус',
        options: {
          filter: approvalQueue ? false : {
            name: 'status_id',
            type: 'multi-select',
            sourceName: 'outlet_update_request_status',
            onOptionsFetch: (statuses: EntityBase[]) =>
              roleName === ROLES.CHIEF || roleName === ROLES.TRADE_MARKETING
                ? statuses.filter((status) => Number(status.id) !== 4)
                : statuses
          },
          sort: false,
          customBodyRender: (status: EntityBase) => status?.id === 4 ? 'Согласование' : status?.name,
        },
      },
      {
        name: 'outlet_id',
        label: 'Действия',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (id, { rowData }) =>  {
            return (
              <>
                {canEdit && rowData.status.id === 1 && <IconButton
                  onClick={() => {
                    history.push(`/acl/outlets/${id}/update`, {
                      update: rowData,
                      back: window.location.pathname + window.location.search,
                    });
                  } } aria-label="edit"
                >
                  <PlayArrow />
                </IconButton>}
                <IconButton
                  onClick={() => history.push(`/acl/updates-outlets/request/${rowData.id}`, {
                    back: window.location.pathname + window.location.search,
                  })}
                  aria-label="Просмотреть заявку"
                ><FaEye /></IconButton>
              </>
            );
          },
        },
      },
      {
        name: 'division',
        label: 'Дивизион',
        options: {
          hidden: true,
          sort: false,
          filter: {
            name: 'division_id',
            type: 'multi-select',
            sourceName: 'division',
          },
        }
      }
    );

    return columns;
  }, [canEdit, history, roleName, approvalQueue]);
};
